import { AsyncPipe, Location, NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ChartType } from 'app/models/charts.model';
import { User } from 'app/models/user';
import { ActivityApi } from 'app/modules/dashboard/api/activity.api';
import {
  ActivityDetails,
  ActivityInformations,
  ActivityInformationsHTTPResponse,
  Session,
  TrainingData,
} from 'app/modules/dashboard/models/activity.model';
import { ActivityMode } from 'app/modules/dashboard/models/timeline.model';
import { ChronoPipe } from 'app/modules/dashboard/pipes/chrono.pipe';
import { WorkoutService } from 'app/services/workout.service';
import { Chart, ChartData, registerables } from 'chart.js';
import { SkeletonModule } from 'primeng/skeleton';
import {
  BehaviorSubject,
  catchError,
  first,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  throwError,
} from 'rxjs';

import { CoachingApi } from '../../api/coaching.api';
import { VideoApi } from '../../api/video.api';
import { BrowseTypesEnum } from '../../models/browse-types.enum';
import { BlockHeaderComponent } from '../scenic/components/blockHeader/blockHeader.component';
import { TrainingLogActionsBlockComponent } from './components/tl-actions-block/tl-actions-block.component';
import { TrainingLogActivityBlockComponent } from './components/tl-activity-block/tl-activity-block.component';
import { TrainingLogChartComponent } from './components/tl-chart/tl-chart.component';
import { TrainingLogPPRChartComponent } from './components/tl-ppr-chart/tl-ppr-chart.component';
import {
  TrainingLogTrainingSectionChartComponent,
  TrainingSection,
} from './components/tl-ts-chart/tl-ts-chart.component';
import { WorkoutChartComponent } from './components/workout-chart/workout-chart.component';
import { FavoriteApi } from '../../api/favorite.api';
import {
  Coaching,
  ScenicCardComponent,
  VideoSection,
  Workout,
  WorkoutInterval,
  IconComponent,
  BlackColorsRef,
  ButtonComponent,
  RankingBlockComponent,
  HeaderBlockComponent,
  Top3,
  MainColorsRef,
} from '@kinomap-dev/ng-ui-lib';
import { Store } from '@ngrx/store';
import { selectProfileData, selectMeUnit } from 'app/store/me/me.selectors';
import { HttpErrorResponse } from '@angular/common/http';
import {
  configureConnectionModal,
  configureRankingModal,
} from 'app/helpers/modal-helpers';
import { SwarmLoginService } from 'app/services';
import { ModalService } from 'app/services/modal.service';
import { GpsOnlyService } from '../../services/gps-only.service';
import { GPSTrack } from 'app/modules/dashboard/models/gps-track.model';
import { DurationPipe } from 'app/modules/dashboard/pipes/duration.pipe';

Chart.register(...registerables);

interface ChartPreference {
  color: string;
  metric: string;
  name: ChartId;
  type: ChartType;
}

export enum ChartId {
  CADENCE = 'cadence',
  ELEVATION = 'elevation',
  POWER = 'power',
  PPR = 'ppr',
  SPEED = 'speed',
  HEART_RATE = 'heartrate',
}

@Component({
  imports: [
    SkeletonModule,
    TrainingLogActionsBlockComponent,
    TrainingLogChartComponent,
    TrainingLogTrainingSectionChartComponent,
    TrainingLogPPRChartComponent,
    RankingBlockComponent,
    BlockHeaderComponent,
    AsyncPipe,
    TranslateModule,
    TrainingLogActivityBlockComponent,
    WorkoutChartComponent,
    NgTemplateOutlet,
    ScenicCardComponent,
    IconComponent,
    HeaderBlockComponent,
    ButtonComponent,
    DurationPipe,
  ],
  providers: [DurationPipe],
  selector: 'app-training-log-page',
  standalone: true,
  styleUrls: ['./training-log.component.scss'],
  templateUrl: './training-log.component.html',
})
export class TrainingLogPageComponent implements OnDestroy, OnInit {
  private loginService: SwarmLoginService = inject(SwarmLoginService);
  private modal: ModalService = inject(ModalService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private activityApi: ActivityApi = inject(ActivityApi);
  private favoriteApi: FavoriteApi = inject(FavoriteApi);
  private videoApi: VideoApi = inject(VideoApi);
  private coachingApi: CoachingApi = inject(CoachingApi);
  private gpsService: GpsOnlyService = inject(GpsOnlyService);
  private workoutService: WorkoutService = inject(WorkoutService);
  private store: Store = inject(Store);
  private location: Location = inject(Location);
  private durationPipe: DurationPipe = inject(DurationPipe);

  isPrivate: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();
  activity: ActivityInformations;
  activityDetails: ActivityDetails;
  activityId: number;
  cadenceChart: any;
  black200: string = BlackColorsRef.black_200;
  cadenceChartsPreferences: ChartPreference = {
    color: '#57A3B0',
    metric: 'rpm',
    name: ChartId.CADENCE,
    type: ChartType.LINE,
  };
  cadenceDataChart: ChartData<any>;
  heartRateDataChart: ChartData<any> | undefined;
  cadenceProgress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  chronoPipe: ChronoPipe;
  coachingCardData: Coaching | undefined;
  completion_percent: number = 0;
  currentUser$: Observable<User>;
  elevationChart: any;
  elevationChartsPreferences: ChartPreference = {
    color: '#57C063',
    metric: 'm',
    name: ChartId.ELEVATION,
    type: ChartType.LINE,
  };
  elevationDataChart: ChartData<any>;
  elevationProgress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  exportTypes: string[] = ['gpx', 'tcx', 'pwx'];
  feetValue: number = 3.28084;
  hoveredIndex: null | number = null;
  likeCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  liked: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  likeProgress: boolean = false;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  milesValue: number = 1.62137119223733396961743418436332;
  powerChart: any;
  userIsAuthor: boolean = false;
  isEditScenic: boolean = false;

  powerChartsPreferences: ChartPreference = {
    color: '#FFEB3B',
    metric: 'watts',
    name: ChartId.POWER,
    type: ChartType.LINE,
  };
  powerDataChart: ChartData<any>;
  powerProgress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  pprChart: any;
  pprChartsPreferences: ChartPreference = {
    color: '#428FF3',
    metric: 'watts',
    name: ChartId.PPR,
    type: ChartType.BAR,
  };
  heartRateChartsPreferences: ChartPreference = {
    color: MainColorsRef.danger,
    metric: 'bmp',
    name: ChartId.HEART_RATE,
    type: ChartType.LINE,
  };
  pprDataChart: ChartData<any>;
  pprProgress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  // 📈 Chart variables
  speedChart: any;
  speedChartsPreferences: ChartPreference = {
    color: '#428FF3',
    metric: 'km/h',
    name: ChartId.SPEED,
    type: ChartType.LINE,
  };
  speedDataChart: ChartData<any>;
  speedProgress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  status: BehaviorSubject<'private' | 'public'> = new BehaviorSubject<
    'private' | 'public'
  >('public');
  subscriptions: Subscription[] = [];
  trainingLogInfos: { data: any; icon: string; label: string }[];
  trainingSections: TrainingSection[] = [];
  unit: 'imperial' | 'metric' = 'metric';
  video: undefined | VideoSection;
  workoutCardData: undefined | Workout;
  workoutIntervals: BehaviorSubject<WorkoutInterval[]> = new BehaviorSubject<
    WorkoutInterval[]
  >(null);
  modalRenderedSubscription: Subscription | null = null;
  top3$: Observable<Top3>;
  gpsTrack: GPSTrack = null;
  userIsAdmin: boolean = false;

  private initWorkoutCardData(): void {
    const lastSession: Session =
      this.activity.sessions[this.activity.sessions_count - 1];
    this.workoutCardData = {
      author: {
        avatar_url: lastSession.user.avatar_url,
        id: lastSession.user_id,
        username: lastSession.user.username,
      },
      avg: this.activity.speed.avg,
      comments_count: lastSession.comments_count,
      description: null,
      duration: this.activity.duration,
      id: this.activity.id,
      thumbnails:
        this.activity.thumbnails.large?.replace('_dev', '') ??
        this.activity.thumbnails.medium?.replace('_dev', '') ??
        'assets/images/activity/free_ride-bg.jpg',
      is_favorite: lastSession.has_liked,
      likes_count: lastSession.likes_count,
      max: this.activity.speed.max,
      min: this.activity.speed.min,
      mode: 'workout',
      name: this.activity.name,
      type: BrowseTypesEnum.Workout,
    } as unknown as Workout;
  }

  /**
   * Initialise les graphiques
   */
  initLineCharts(): void {
    /**
     * Transforme la reponse de details en un object ChartData
     * pour la donnée de 🏎️ SPEED
     */
    this.speedDataChart = {
      datasets: [
        {
          data: this.activityDetails.training_data.map((data: TrainingData) => {
            return data.speed;
          }),
        },
      ],
      labels: this.activityDetails.training_data.map((data: TrainingData) => {
        return data.position;
      }),
    };

    /**
     * Transforme la reponse de details en un object ChartData
     * pour la donnée de la ⚡PUISSANCE
     */
    this.powerDataChart = {
      datasets: [
        {
          data: this.activityDetails.training_data.map((data: TrainingData) => {
            return data.power;
          }),
        },
      ],
      labels: this.activityDetails.training_data.map((data: TrainingData) => {
        return data.position;
      }),
    };

    /**
     * Transforme la reponse de details en un object ChartData
     * pour la donnée de 🏎️ CADENCE
     */
    this.cadenceDataChart = {
      datasets: [
        {
          data: this.activityDetails.training_data.map((data: TrainingData) => {
            return data.cadence;
          }),
        },
      ],
      labels: this.activityDetails.training_data.map((data: TrainingData) => {
        return data.position;
      }),
    };

    this.heartRateDataChart = this.activityDetails.has_heart_rate
      ? {
          datasets: [
            {
              data: this.activityDetails.training_data.map(
                (data: TrainingData) => {
                  return data.heartrate;
                }
              ),
            },
          ],
          labels: this.activityDetails.training_data.map(
            (data: TrainingData) => {
              return data.position;
            }
          ),
        }
      : undefined;

    /**
     * Transforme la reponse de details en un object ChartData
     * pour la donnée de l'elevation 🚵 ⬆️
     */
    this.elevationDataChart = {
      datasets: [
        {
          data: this.activityDetails.training_data.map((data: TrainingData) => {
            return data.elevation;
          }),
        },
      ],
      labels: this.activityDetails.training_data.map((data: TrainingData) => {
        return data.position;
      }),
    };

    /**
     * Transform la reponse de details en un object ChartData
     * pour la donnée du PPR 🔋🪫
     */
    this.pprDataChart = {
      datasets: [
        {
          data: this.activityDetails.ppr.map(
            (data: { x: number; y: number }) => {
              return data.y;
            }
          ),
        },
      ],
      labels: this.activityDetails.ppr.map((data: { x: number; y: number }) => {
        return data.x;
      }),
    };
  }

  /**
   * Pour afficher les infos sur la thumbnail du bloc activité
   */
  initTrainingLogInfo(): void {
    this.trainingLogInfos = [
      {
        data:
          this.unit === 'metric'
            ? (this.activity.distance / 1000).toFixed(1)
            : (this.activity.distance / 1000 / this.milesValue).toFixed(1),
        icon: 'distance',
        label: this.unit === 'metric' ? 'km' : 'miles',
      },
      {
        data: this.durationPipe.transform(this.activity.duration, 'value'),
        icon: 'timer',
        label: this.durationPipe.transform(this.activity.duration, 'unit'),
      },
      {
        data:
          this.unit === 'metric'
            ? this.activity.speed.avg.toFixed(1)
            : (this.activity.speed.avg / this.milesValue).toFixed(1),
        icon: 'kmh',
        label: this.unit === 'metric' ? 'km/h' : 'mph',
      },
    ];

    if (
      this.activity.mode ===
      (ActivityMode.DISCOVERY ||
        ActivityMode.CHALLENGE ||
        ActivityMode.GPS_ONLY ||
        ActivityMode.FREE_RIDE)
    ) {
      this.trainingLogInfos = [
        ...this.trainingLogInfos,
        {
          data:
            this.unit === 'metric'
              ? this.activity.altitude.max.toFixed(0)
              : (this.activity.altitude.max * this.feetValue).toFixed(0),
          icon: 'slope',
          label: this.unit === 'metric' ? 'm' : 'feets',
        },
      ];
    }
  }

  initTrainingSectionChart(
    trainingSections: TrainingSection[]
  ): TrainingSection[] {
    if (!trainingSections?.length) return [];

    const ts: TrainingSection[] = [];
    let index: number = 0;

    trainingSections.forEach((_ts: TrainingSection, i: number) => {
      if (_ts.position <= this.activity.duration && trainingSections[i + 1]) {
        ts.push(_ts);
        index = i;
      }
    });

    const position: number = this.activity.duration ?? this.activity.distance;
    const b1: TrainingSection = trainingSections[index];

    if (trainingSections[index + 1]) {
      const b3: TrainingSection = trainingSections[index + 1];
      const lastPoint: TrainingSection = this.interpolateTrainingSection(
        b1,
        b3,
        position
      );
      ts.push(lastPoint);
    } else {
      ts.push({
        ...b1,
        position: position,
      });
    }

    return ts;
  }

  interpolateTrainingSection(
    b1: TrainingSection,
    b2: TrainingSection,
    position: number
  ): TrainingSection {
    if (!b1 || !b2) {
      throw new Error('Both training sections are required for interpolation');
    }

    const interpolatedSection: TrainingSection = {
      average_power: 0,
      distance: 0,
      elevation: 0,
      polyline: '',
      position: position,
      slope: 0,
      workout_load: 0,
    };

    const y1: number = b1.position;
    const y3: number = b2.position;
    const x1: number = b1.elevation;
    const x3: number = b2.elevation;

    if (y3 === y1) return { ...b1, position };

    const interpolatedElevation: number = Number(
      (x1 + ((x3 - x1) * (position - y1)) / (y3 - y1)).toFixed(1)
    );
    interpolatedSection.elevation = interpolatedElevation;

    return interpolatedSection;
  }

  loadActivityData(activityId: string): void {
    this.loading$.next(true);

    this.activityApi
      .getActivityInformations(activityId)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((actInfo: ActivityInformationsHTTPResponse) => {
          if (!actInfo || !actInfo.data) {
            throw new Error('No activity information found');
          }

          const commonObservables: Observable<any>[] = [
            of(actInfo),
            this.activityApi.getActivityDetails(activityId),
            this.store.select(selectMeUnit).pipe(first()),
          ];

          switch (actInfo.data.mode) {
            case ActivityMode.CHALLENGE:
            case ActivityMode.DISCOVERY:
              commonObservables.push(
                this.videoApi.getTrainingSections(actInfo.data.video_id),
                this.videoApi.getVideoInformationV4(actInfo.data.video_id)
              );
              break;

            case ActivityMode.COACHING:
              commonObservables.push(
                this.coachingApi.getCoaching$(actInfo.data.video_id)
              );
              break;

            case ActivityMode.GPS_ONLY:
              commonObservables.push(
                this.gpsService.getInformation(actInfo.data.gps_only_id)
              );
              break;

            case ActivityMode.WORKOUT:
              commonObservables.push(of(null), of(null));
              break;

            case ActivityMode.FREE_RIDE:
              commonObservables.push(of(null), of(null));

            default:
              console.warn('Mode not handled:', actInfo.data.mode);
              commonObservables.push(of(null), of(null));
              break;
          }

          return forkJoin(commonObservables);
        }),
        map(([actInfo, actDetails, userUnit, videoSections, videoInfo]) => {
          return {
            actDetails: actDetails.data,
            actInfo: actInfo.data,
            userUnit,
            videoInfo,
            videoSections,
          };
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404 || error.status === 401) {
            this.isPrivate = true;
            console.warn('Activity is private or does not exist');
            this.loading$.next(false);
            return;
          }

          this.loading$.next(false);
          return throwError(() => new Error('Failed to load activity data'));
        })
      )
      .subscribe({
        error: (err: HttpErrorResponse) => {
          this.loading$.next(false);
        },
        next: (data) => {
          this.activity = data.actInfo;
          this.activityDetails = data.actDetails;
          this.unit = data.userUnit;
          this.video = data.videoInfo;
          this.status.next(data.actInfo.status);
          this.initTrainingLogInfo();

          this.currentUser$.subscribe((user: User) => {
            if (user) {
              this.userIsAuthor = user.id === this.activity.sessions[0].user_id;
              this.userIsAdmin = user.is_admin;
            }
          });

          if (ActivityMode.GPS_ONLY === this.activity.mode) {
            this.gpsTrack = data.videoSections;
          }

          if (
            [ActivityMode.CHALLENGE, ActivityMode.DISCOVERY].includes(
              this.activity.mode
            )
          ) {
            this.trainingSections = this.initTrainingSectionChart(
              data.videoSections
            );
          }
          this.initLineCharts();

          if (
            !this.userIsAuthor &&
            this.activity.status === 'private' &&
            !this.userIsAdmin
          ) {
            this.router.navigate(['/404']);
            return;
          }

          if (this.activity.mode === 'workout') {
            this.workoutService.getWorkout(this.activity.workout_id).subscribe({
              next: (workout: Workout) => {
                this.workoutIntervals.next(workout.intervals);
              },
            });
            this.initWorkoutCardData();
          }

          if (this.activity.mode === 'coaching') {
            this.coachingApi
              .getCoaching$(this.activity.video_id)
              .subscribe(
                (coaching: Coaching) => (this.coachingCardData = coaching)
              );
          }
          this.getTop3();
          this.loading$.next(false);
        },
      });
  }

  addVideoToFavorite(videoId: number): void {
    if (this.video.is_favorite) {
      this.favoriteApi
        .deleteFavorite$(videoId, BrowseTypesEnum.Video)
        .subscribe(() => (this.video.is_favorite = false));
      return;
    }
    this.favoriteApi
      .postFavorite$(videoId, BrowseTypesEnum.Video)
      .subscribe(() => (this.video.is_favorite = true));
  }

  onAuthorCardClick(authorId: number): void {
    this.router.navigate(['/dashboard/profile', authorId]);
  }

  onVideoCardClick(videoId: number): void {
    this.router.navigate(['/browse/watch', videoId]);
  }

  redirect(id: number, to: 'author' | 'browse'): Promise<boolean> {
    switch (to) {
      case 'author': {
        return this.router.navigate(['/dashboard/profile', id]);
      }
      case 'browse': {
        return this.router.navigate(['/browse/coaching', id]);
      }
    }
  }

  navBack(): void {
    this.location.back();
  }

  navToActivity(activityId: number): void {
    this.router.navigate(['browse', 'training-log', activityId]).then(() => {
      this.location.go(this.router.url);
      window.location.reload();
    });
  }

  getTop3(): void {
    if (this.activity.mode === ActivityMode.GPS_ONLY) {
      this.top3$ = this.activityApi.getMapModeActivityTop(this.gpsTrack?.id);
    }

    if (
      [ActivityMode.CHALLENGE, ActivityMode.DISCOVERY].includes(
        this.activity.mode
      )
    ) {
      this.top3$ = this.activityApi.getActivityTop(this.video?.id);
    }
  }

  openModal(
    modalType: 'ranking' | 'connection',
    data: any = {},
    user: User
  ): void {
    this.modal.reset();
    this.modalRenderedSubscription?.unsubscribe();
    const modalClassName: string = modalType + '-modal';
    this.modal.display(modalClassName);
    const modalConfig = {
      ranking: () =>
        configureRankingModal(
          this.modal,
          data,
          this.activity.mode === ActivityMode.GPS_ONLY
            ? this.gpsTrack
            : this.video,
          user,
          modalClassName
        ),
      connection: () => configureConnectionModal(this.modal, this.loginService),
    };

    setTimeout(
      () => (this.modalRenderedSubscription = modalConfig[modalType]?.())
    );
  }

  // 💥 ON DESTROY
  ngOnDestroy(): void {
    this.modalRenderedSubscription?.unsubscribe();
    this.modal.reset();
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  ngOnInit(): void {
    this.currentUser$ = this.store.select(selectProfileData);
    const activityId: string = this.route.snapshot.paramMap.get('activityId');

    if (activityId) {
      return this.loadActivityData(activityId);
    }

    console.warn('Param is not defined, navigating to 404');
    this.router.navigate(['/404']);
  }
}
